/*
 *   File : manage-sub-admin.js
 *   Author : https://evoqins.com
 *   Description : manage sub admin component
 *   Integrations : sub-admin/list, orders/filter-data, sub-admin/roles/delete
 *   Version : 1.1.0
 */
import React, { useEffect, useState, useRef } from "react";
import { PrimaryButton } from "../../Components/Buttons";
import { Filter } from "../../Components/Filters";
import { DataTableComponent } from "../../Components/DataTable";
import { useDispatch, useSelector } from "react-redux";
import {
  setPageTitle,
  setSearchEnabled,
  setPlaceHolder,
  clearSearchQuery,
} from "../../Redux/Ui/ui.slice";
import { useNavigate } from "react-router-dom";
import { APIService } from "../../Service";
import { Loader, EmptyScreen } from "../../Components/Other";
import { Tabs } from "../../Components/Navigation";
import { ConfirmationModal } from "../../Components/Modals";
import { toast } from "react-toastify";
import { usePersistedFilter } from "../../Hooks/usePersistedFilter";
import {
  clearFilter,
  clearListState,
  saveListState,
  LIST_STATE_KEY,
} from "../../Redux/Filter/filterSlice";

const TAB_DATA = [
  {
    id: 0,
    label: "sub_admin",
    name: "Sub Admin",
  },
  {
    id: 1,
    label: "modules",
    name: "Modules",
  },
];

export default function ManageSubAdmin() {
  const navigate = useNavigate();

  const [loader, setLoader] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [tableData, setTableData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [roleData, setRoleData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);

  const listFetchIdRef = useRef(0);

  //Redux title and searchbar setting
  const dispatch = useDispatch();

  // Tab, page & page size live in the Redux store so returning from detail restores them
  const listState = useSelector(
    (state) => state.persistedFilters[LIST_STATE_KEY]?.["manage-sub-admin"],
  );
  const curTab = listState?.tab ?? 0;
  const page = listState?.page ?? 1;
  const perPage = listState?.perPage ?? 10;

  const _updateListParams = (next) => {
    dispatch(saveListState("manage-sub-admin", next));
  };
  const setPage = (p) => _updateListParams({ page: p });

  const {
    filterRef,
    savedSelections,
    handleApply: persistApply,
  } = usePersistedFilter("manage-sub-admin");

  useEffect(() => {
    dispatch(setPageTitle(["Manage Sub Admin"]));
    dispatch(setSearchEnabled(true));
    dispatch(setPlaceHolder("Search Sub Admin Name"));
    return () => {
      listFetchIdRef.current += 1;
      dispatch(clearSearchQuery());
    };
  }, [dispatch]);

  // Modules tab does not use global search; clear so text does not linger in the header
  useEffect(() => {
    if (curTab !== 0) {
      dispatch(clearSearchQuery());
    }
  }, [curTab, dispatch]);

  // searchQuery from global UI (same as orders.js)
  const searchQuery = useSelector((state) => state.ui.searchQuery);

  useEffect(() => {
    _getFilterData();
  }, []);

  useEffect(() => {
    if (savedSelections) {
      setAppliedFilters(savedSelections);
    }
  }, []);

  // Clear persisted filters & list state when leaving (except to sub admin detail).
  // Deferred so StrictMode's simulated unmount/remount doesn't wipe them.
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      setTimeout(() => {
        if (isMountedRef.current) return;
        const goingToDetail =
          window.location.pathname.startsWith("/manage-sub-admin/detail");
        if (!goingToDetail) {
          dispatch(clearFilter("manage-sub-admin"));
          dispatch(clearListState("manage-sub-admin"));
        }
      });
    };
  }, []);

  useEffect(() => {
    if (curTab === 0) return;
    _getRoleLists();
  }, [curTab]);

  // A new search must start from page 1, otherwise results beyond page 1 look empty
  const prevSearchRef = useRef(searchQuery);
  useEffect(() => {
    if (prevSearchRef.current !== searchQuery) {
      prevSearchRef.current = searchQuery;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    _getSubAdminList();
  }, [appliedFilters, searchQuery, page, perPage]);

  const COLUMNS = [
    {
      name: "Name",
      wrap: true,
      width: "200px",
      selector: (row) => row.username,
      cell: (row) => {
        const name = row.username || "NA";
        const initials =
          row.username?.trim()?.slice(0, 2)?.toUpperCase() || "NA";
        return (
          <div className="d-flex column-gap-2 align-items-center">
            {row.image ? (
              <img
                src={row.image}
                alt={name}
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                className={`d-flex align-items-center justify-content-center ${row.profile_image ? " bg-secondary text-white fw-semibold" : " text-black fw-semibold"}`}
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  fontSize: "10px",
                }}
              >
                {initials}
              </div>
            )}

            <span>{row.username || "N/A"}</span>
          </div>
        );
      },
    },
    {
      name: "Email",
      width: "240px",
      selector: (row) => row.email,
    },
    {
      name: "Phone Number",
      width: "180px",
      selector: (row) => row.mobile_number,
    },
    {
      name: "Role",
      width: "160px",
      selector: (row) => row.role,
    },
    {
      name: "Modules",
      width: "240px",
      cell: (row) => (
        <p className="mb-0 py-1 text-nowrap d-flex flex-wrap">
          {row.modules && row.modules.length > 0
            ? row.modules.map((module) => module.name).join(", ")
            : "-"}
        </p>
      ),
      sortable: false,
    },
    {
      name: "Status",
      selector: (row) => row.block_status,
      cell: (row) => (
        <span
          className={!row.is_blocked ? "text-medium-sea-green" : "text-cg-red"}
        >
          {row.is_blocked ? "Inactive" : "Active"}
        </span>
      ),
    },
    {
      name: "Action",
      width: "100px",
      cell: (row) => (
        <PrimaryButton
          ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue "
          name="Edit"
          onPress={() => {
            navigate(`/manage-sub-admin/edit/${row.admin_id}`);
          }}
        />
      ),
    },
    {
      width: "150px",
      cell: (row) => (
        <PrimaryButton
          ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue "
          name="View details"
          onPress={() => {
            navigate(`/manage-sub-admin/detail/${row.admin_id}`);
          }}
        />
      ),
    },
  ];

  const ROLE_COLUMNS = [
    {
      name: "Name",
      width: "220px",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Modules",
      width: "300px",
      cell: (row) => (
        <p className="mb-0 py-1 text-nowrap d-flex flex-wrap">
          {row.modules && row.modules.length > 0
            ? row.modules.map((module) => module.name).join(", ")
            : "-"}
        </p>
      ),
      sortable: false,
    },
    {
      name: "Status",
      width: "150px",
      selector: (row) => row.status,
      cell: (row) => (
        <span className={row.status ? "text-medium-sea-green" : "text-cg-red"}>
          {row.status ? "Active" : "Inactive"}
        </span>
      ),
      sortable: false,
    },
    {
      name: "Action",
      width: "120px",
      cell: (row) => (
        <div className="d-flex column-gap-2">
          <PrimaryButton
            ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue"
            name="Edit"
            onPress={() => {
              navigate(`/manage-sub-admin/roles/edit/${row.id}`);
            }}
          />
          <PrimaryButton
            ClassName="bg-transparent fs-14 fw-500 p-0 rounded-0 text-cg-red e-border-bottom-dashed-blue"
            name="Delete"
            onPress={() => {
              setSelectedRoleId(row.id);
              setShowModal(true);
            }}
          />
        </div>
      ),
    },
  ];

  //service to get filterData (normalize so Filter always receives an array; support multiple API shapes)
  const _getFilterData = () => {
    APIService(false, "sub-admin/filter-data")
      .then((res) => {
        if (!res) return;
        const raw = res.data ?? res.filters ?? res.sub_admins ?? res;
        const list = Array.isArray(raw)
          ? raw
          : (raw?.filters ??
              raw?.data ??
              raw?.items ??
              raw?.filter_options ??
              raw?.sub_admins ??
              []) || [];
        setFilterData(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error("Failed to load sub-admin filter data:", err);
      });
  };

  //service to get Role List
  const _getRoleLists = () => {
    setLoader(true);
    const payload = { page_size: 12, query: null };
    APIService(true, "sub-admin/role/list", payload)
      .then((res) => {
        if (res.status_code === 200) {
          console.log(res);
          setRoleData(res.data.data);
        }
      })
      .catch((err) => {
        console.error("Failed to load orders filter data:", err);
      })
      .finally(() => setLoader(false));
  };

  //service to get sub admin list (server side)
  const _getSubAdminList = () => {
    const fetchId = ++listFetchIdRef.current;
    setLoader(true);

    // With a search or filter active, fetch from page 1 without page_size
    const hasFilters = Object.values(appliedFilters).some((value) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value),
    );
    const isFiltered = Boolean(searchQuery) || hasFilters;
    const payload = {
      query: searchQuery || "",
      ...(isFiltered ? { page: 1 } : { page: page, page_size: perPage }),

      // filters coming from Filter component
      ...appliedFilters,
    };

    APIService(true, "sub-admin/list", payload)
      .then((res) => {
        if (fetchId !== listFetchIdRef.current) return;
        if (res.status_code === 200) {
          const subAdmins = res.data?.data || [];
          setTableData(subAdmins);
          setTotalRows(res.data?.total_count || subAdmins.length);
          setIsEmpty(subAdmins.length === 0);
        } else {
          setTableData([]);
          setIsEmpty(true);
        }
      })
      .catch((err) => {
        if (fetchId !== listFetchIdRef.current) return;
        console.error("Sub admin list error:", err);
        setTableData([]);
        setIsEmpty(true);
      })
      .finally(() => {
        if (fetchId === listFetchIdRef.current) {
          setLoader(false);
        }
      });
  };

  //service to delete role
  const _deleteRole = () => {
    if (!selectedRoleId) return;

    const payload = {
      role_id: selectedRoleId,
    };

    APIService(true, "sub-admin/roles/delete", payload)
      .then((res) => {
        if (res.status_code === 200) {
          console.log("Role deleted successfully");
          _getRoleLists();
          setShowModal(false);
          setSelectedRoleId(null);
          toast.success(res.message);
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        console.error("Failed to delete role:", err);
        setShowModal(false);
        toast.error(res.message);

        setSelectedRoleId(null);
      });
  };

  function _onFilterApply(selectedValues) {
    console.log("Filter applied:", selectedValues);
    setAppliedFilters(selectedValues);
    setPage(1);
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-column row-gap-3 pt-3">
          <div className="d-flex justify-content-end">
            <PrimaryButton
              ClassName="rounded-16  bg-midnight-blue lh-24 py-2 px-3 text-white fs-16 fw-400"
              name={curTab === 0 ? "Add Sub admin" : "Create Module"}
              onPress={() =>
                navigate(
                  curTab === 0
                    ? "/manage-sub-admin/add"
                    : "/manage-sub-admin/roles/add",
                )
              }
            />
          </div>
          <div className="e-glass-card rounded-16 d-flex flex-column rg-12 bg-transparent">
            <div className="row pt-24 pb-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center px-24">
                  <Tabs
                    data={TAB_DATA}
                    defaultActive={curTab}
                    onChange={(activeId) =>
                      _updateListParams({ tab: activeId, page: 1 })
                    }
                  />
                  {curTab === 0 && (
                    <Filter
                      ref={filterRef}
                      filters={filterData}
                      initialSelections={savedSelections}
                      onApply={(sel) =>
                        persistApply(sel, _onFilterApply)
                      }
                    />
                  )}
                </div>
              </div>
            </div>
            {loader ? (
              <Loader />
            ) : isEmpty ? (
              <EmptyScreen
                message={
                  curTab === 0 ? "No Sub Admins Found" : "No Modules Found"
                }
              />
            ) : curTab === 0 ? (
              <div className="row">
                <div className="col-12">
                  <DataTableComponent
                    info={true}
                    columns={COLUMNS}
                    data={tableData}
                    pagination={true}
                    paginationServer={true}
                    currentPage={page}
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    onChangePage={(p) => setPage(p)}
                    onChangeRowsPerPage={(newPerPage) =>
                      _updateListParams({ page: 1, perPage: newPerPage })
                    }
                    className="e-table-basic"
                  />
                </div>
              </div>
            ) : (
              <div className="row">
                <div className="col-12">
                  <DataTableComponent
                    info={false}
                    columns={ROLE_COLUMNS}
                    data={roleData}
                    pagination={true}
                    paginationServer={true}
                    currentPage={page}
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    onChangePage={(p) => setPage(p)}
                    onChangeRowsPerPage={(newPerPage) =>
                      _updateListParams({ page: 1, perPage: newPerPage })
                    }
                    className="e-table-basic"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmationModal
        open={showModal}
        title="Delete Role"
        message="Are you sure you want to delete this role?"
        onConfirm={_deleteRole}
        onCancel={() => {
          setShowModal(false);
          setSelectedRoleId(null);
        }}
      />
    </div>
  );
}